import React, { useEffect, useState } from 'react';
import Header from './Header';
import { useInformeBySlug } from '../../../hooks/useWordPressData';
import useCurrentSlug from '../../../hooks/useCurrentSlug';
import { reportData } from '../../../data/report-data/report-data/reportData';
import slugify from 'react-slugify';

/**
 * Componente que genera dinámicamente las URLs del header basándose en el contenido de WordPress
 * @param {Object} props - Props del componente
 * @param {Object} props.myRoutes - Rutas de la aplicación
 * @param {string} props.category - Categoría de la sección (ciberseguridad, interconexion, ipv6)
 * @returns {JSX.Element} - Componente Header con URLs dinámicas
 */
const DynamicWordPressHeader = ({ myRoutes, category }) => {
    const currentSlug = useCurrentSlug();
    
    // Obtener el contenido de WordPress para el slug actual (en el idioma actual)
    const { informe } = useInformeBySlug(currentSlug, true, 'es'); // Siempre obtener en español primero
    
    // Estados para almacenar los títulos y slugs en diferentes idiomas
    const [translations, setTranslations] = useState({
        es: { title: '', slug: '' },
        en: { title: '', slug: '' },
        pt: { title: '', slug: '' }
    });

    console.log('currentSlug', currentSlug);
    // Función para obtener slugs desde reportData basándose en el slug actual
    const getSlugsFromReportData = (currentSlug) => {
        // Buscar en reportData si algún reporte tiene títulos que generen el slug actual
        for (const [, reportContent] of Object.entries(reportData)) {
            // Generar slugs para cada idioma y ver si alguno coincide con el actual
            const generatedSlugs = {
                es: slugify(reportContent.es?.title || ''),
                en: slugify(reportContent.en?.title || ''),
                pt: slugify(reportContent.pt?.title || '')
            };

            // Si el slug actual coincide con alguno de los generados, devolver todos los slugs
            if (generatedSlugs.es === currentSlug || 
                generatedSlugs.en === currentSlug || 
                generatedSlugs.pt === currentSlug) {
                return generatedSlugs;
            }
        }
        return null;
    };

    // Efecto para extraer las traducciones del informe de WordPress
    useEffect(() => {
        if (currentSlug) {
            const reportDataSlugs = getSlugsFromReportData(currentSlug);
            
            // Mapear las traducciones por idioma
            let newTranslations = {
                es: { title: '', slug: currentSlug },
                en: { title: '', slug: '' },
                pt: { title: '', slug: '' }
            };

            // Si tenemos el informe de WordPress, usar sus datos
            if (informe) {
                const wpmlTranslations = informe.wpml_translations || [];
                
                newTranslations.es = {
                    title: informe.title || '',
                    slug: currentSlug
                };

                // Buscar traducciones en wpml_translations
                wpmlTranslations.forEach(translation => {
                    if (translation.locale === 'en_US') {
                        newTranslations.en = {
                            title: translation.post_title || '',
                            slug: translation.slug || ''
                        };
                    } else if (translation.locale === 'pt_BR') {
                        newTranslations.pt = {
                            title: translation.post_title || '',
                            slug: translation.slug || ''
                        };
                    }
                });
            }

            // Si no encontramos traducciones en WordPress, usar reportData
            if (!newTranslations.en.slug && reportDataSlugs) {
                newTranslations = {
                    es: { title: '', slug: reportDataSlugs.es },
                    en: { title: '', slug: reportDataSlugs.en },
                    pt: { title: '', slug: reportDataSlugs.pt }
                };
            }

            setTranslations(newTranslations);
        }
    }, [currentSlug, informe]);

    // Función para generar slug a partir del título (no usada, pero útil para debug)
    // const generateSlug = (title) => {
    //     if (!title) return '';
    //     return title
    //         .toLowerCase()
    //         .replace(/[^a-z0-9\s-]/g, '')
    //         .replace(/\s+/g, '-')
    //         .replace(/-+/g, '-')
    //         .trim();
    // };

    // Generar URLs para cada idioma usando los slugs reales de WordPress
    const url_es = `/es/${myRoutes?.es?.[category]}/${translations.es.slug}`;
    const url_en = `/en/${myRoutes?.en?.[category]}/${translations.en.slug}`;
    const url_pt = `/pt/${myRoutes?.pt?.[category]}/${translations.pt.slug}`;

    // Debug info (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
        const reportDataSlugs = getSlugsFromReportData(currentSlug);
        console.log('🌍 DynamicWordPressHeader DEBUG:', {
            currentSlug,
            category,
            translations,
            url_es,
            url_en,
            url_pt,
            reportDataSlugs,
            informe: informe ? {
                title: informe.title,
                wpml_translations: informe.wpml_translations || []
            } : null,
            myRoutes: myRoutes
        });
        
        // Debug específico para wpml_translations
        if (informe?.wpml_translations) {
            console.log('🔍 WPML Translations:', informe.wpml_translations);
        }
    }

    return (
        <Header
            url_es={url_es}
            url_en={url_en}
            url_pt={url_pt}
        />
    );
};

export default DynamicWordPressHeader;
